import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { SearchIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface Query {
  id: string;
  agentName: string;
  agencyName: string;
  manuscriptTitle: string;
  submittedDate: string;
  submittedDaysAgo: number;
  status: string;
  agentResponseTime: string;
  expectedResponseDate: string;
  hasResponse: boolean;
  responseType: string | null;
  responseDate: string | null;
}

export const MyQueriesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [queries, setQueries] = useState<Query[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQueries();
  }, []);

  const fetchQueries = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.queries.myQueries, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch queries');
        setLoading(false);
        return;
      }

      const transformedQueries = data.queries.map((q: any) => {
        const submittedDate = new Date(q.createdAt);
        const today = new Date();
        const daysAgo = Math.floor((today.getTime() - submittedDate.getTime()) / (1000 * 60 * 60 * 24));

        // Map backend status to frontend status
        let frontendStatus = 'pending';
        let responseType = null;
        if (q.status === 'PENDING' || q.status === 'REVIEWING') {
          frontendStatus = 'pending';
        } else if (q.status === 'REQUESTED') {
          frontendStatus = 'partial-request';
          responseType = 'partial';
        } else if (q.status === 'OFFERED') {
          frontendStatus = 'full-request';
          responseType = 'full';
        } else if (q.status === 'REJECTED') {
          frontendStatus = 'rejected';
          responseType = 'pass';
        }

        // Calculate expected response date (add 6 weeks to submission date)
        const expectedDate = new Date(submittedDate);
        expectedDate.setDate(expectedDate.getDate() + 42); // 6 weeks

        return {
          id: q.id,
          agentName: q.agent?.displayName || 'Unknown Agent',
          agencyName: q.agent?.agencyName || 'Unknown Agency',
          manuscriptTitle: q.manuscript?.title || 'Unknown Manuscript',
          submittedDate: submittedDate.toISOString().split('T')[0],
          submittedDaysAgo: daysAgo,
          status: frontendStatus,
          agentResponseTime: q.agent?.responseTime || '6-8 weeks',
          expectedResponseDate: expectedDate.toISOString().split('T')[0],
          hasResponse: !!q.respondedAt,
          responseType: responseType,
          responseDate: q.respondedAt ? new Date(q.respondedAt).toISOString().split('T')[0] : null,
        };
      });

      setQueries(transformedQueries);
      setLoading(false);
    } catch (err) {
      console.error('Fetch queries error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const filteredQueries = queries.filter((query: Query) => {
    const matchesSearch = query.agentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         query.agencyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         query.manuscriptTitle.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || query.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedQueries = [...filteredQueries].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime();
    } else if (sortBy === 'agent') {
      return a.agentName.localeCompare(b.agentName);
    } else if (sortBy === 'manuscript') {
      return a.manuscriptTitle.localeCompare(b.manuscriptTitle);
    }
    return 0;
  });

  const handleWithdraw = (queryId: string, agentName: string) => {
    if (window.confirm(`Are you sure you want to withdraw your query to ${agentName}? This action cannot be undone.`)) {
      setQueries(queries.filter((q: Query) => q.id !== queryId));
      window.alert('Query withdrawn successfully.');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: any; label: string }> = {
      pending: { variant: 'warning', label: 'Pending' },
      'partial-request': { variant: 'seeking', label: 'Partial Requested' },
      'full-request': { variant: 'success', label: 'Full Requested' },
      rejected: { variant: 'default', label: 'Rejected' },
      'no-response': { variant: 'error', label: 'No Response' },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getDaysUntilResponse = (expectedDate: string) => {
    const today = new Date();
    const expected = new Date(expectedDate);
    const diffTime = expected.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const totalQueries = queries.length;
  const pendingCount = queries.filter((q: Query) => q.status === 'pending').length;
  const requestsCount = queries.filter((q: Query) => q.status === 'partial-request' || q.status === 'full-request').length;
  const rejectedCount = queries.filter((q: Query) => q.status === 'rejected').length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '20px',
        }}>
          <div>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              My Queries
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Track your agent submissions
            </p>
          </div>
          <Button size="large" onClick={() => window.location.href = '/agents'}>
            Find Agents to Query
          </Button>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading your queries...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchQueries}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Quick Stats */}
        {!loading && !error && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}>
          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Total Queries
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.primary,
              fontFamily: theme.fonts.monospace,
            }}>
              {totalQueries}
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Pending
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.warning,
              fontFamily: theme.fonts.monospace,
            }}>
              {pendingCount}
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Requests
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.success,
              fontFamily: theme.fonts.monospace,
            }}>
              {requestsCount}
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Rejected
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.textTertiary,
              fontFamily: theme.fonts.monospace,
            }}>
              {rejectedCount}
            </div>
          </Card>
        </div>
        )}

        {/* Search and Filters */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '32px',
          flexWrap: 'wrap',
        }}>
          {/* Search */}
          <div style={{
            flex: '1',
            minWidth: '300px',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: theme.colors.textTertiary,
            }}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search queries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px 14px 48px',
                fontSize: '16px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.large,
                fontFamily: theme.fonts.primary,
                color: theme.colors.textPrimary,
                backgroundColor: theme.colors.white,
                outline: 'none',
              }}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '14px 56px 14px 16px',
              fontSize: '16px',
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.large,
              fontFamily: theme.fonts.primary,
              color: theme.colors.textPrimary,
              backgroundColor: theme.colors.white,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="partial-request">Partial Requested</option>
            <option value="full-request">Full Requested</option>
            <option value="rejected">Rejected</option>
            <option value="no-response">No Response</option>
          </select>

          {/* Sort By */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '14px 56px 14px 16px',
              fontSize: '16px',
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.large,
              fontFamily: theme.fonts.primary,
              color: theme.colors.textPrimary,
              backgroundColor: theme.colors.white,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="date">Sort by Date</option>
            <option value="agent">Sort by Agent</option>
            <option value="manuscript">Sort by Manuscript</option>
          </select>
        </div>

        {/* Queries List */}
        {sortedQueries.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '20px',
              }}>
                {searchQuery || statusFilter !== 'all'
                  ? 'No queries match your search criteria'
                  : 'You haven\'t submitted any queries yet'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Button onClick={() => window.location.href = '/agents'}>
                  Find Agents to Query
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
            {sortedQueries.map((query) => (
              <Card key={query.id}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                  gap: '16px',
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: '500',
                      marginBottom: '4px',
                      color: theme.colors.textPrimary,
                      cursor: 'pointer',
                    }}
                    onClick={() => window.location.href = `/writer/queries/${query.id}`}
                    >
                      {query.agentName}
                    </h3>
                    <div style={{
                      fontSize: '16px',
                      color: theme.colors.textSecondary,
                      marginBottom: '8px',
                    }}>
                      {query.agencyName}
                    </div>
                  </div>
                  {getStatusBadge(query.status)}
                </div>

                {/* Manuscript */}
                <div style={{
                  fontSize: '15px',
                  color: theme.colors.textPrimary,
                  marginBottom: '16px',
                }}>
                  <span style={{ color: theme.colors.textSecondary }}>Manuscript: </span>
                  <strong>{query.manuscriptTitle}</strong>
                </div>

                {/* Timeline */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  padding: '16px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.medium,
                  marginBottom: '16px',
                }}>
                  <div>
                    <div style={{
                      fontSize: '13px',
                      color: theme.colors.textSecondary,
                      marginBottom: '4px',
                    }}>
                      Submitted
                    </div>
                    <div style={{
                      fontSize: '15px',
                      color: theme.colors.textPrimary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      {new Date(query.submittedDate).toLocaleDateString()} ({query.submittedDaysAgo} days ago)
                    </div>
                  </div>

                  {query.hasResponse && query.responseDate && (
                    <div>
                      <div style={{
                        fontSize: '13px',
                        color: theme.colors.textSecondary,
                        marginBottom: '4px',
                      }}>
                        Response Received
                      </div>
                      <div style={{
                        fontSize: '15px',
                        color: theme.colors.textPrimary,
                        fontFamily: theme.fonts.monospace,
                      }}>
                        {new Date(query.responseDate).toLocaleDateString()}
                      </div>
                    </div>
                  )}

                  {!query.hasResponse && (
                    <div>
                      <div style={{
                        fontSize: '13px',
                        color: theme.colors.textSecondary,
                        marginBottom: '4px',
                      }}>
                        Expected Response
                      </div>
                      <div style={{
                        fontSize: '15px',
                        color: getDaysUntilResponse(query.expectedResponseDate) < 0 ? theme.colors.error : theme.colors.textPrimary,
                        fontFamily: theme.fonts.monospace,
                      }}>
                        {new Date(query.expectedResponseDate).toLocaleDateString()}
                        {getDaysUntilResponse(query.expectedResponseDate) < 0 && ' (overdue)'}
                      </div>
                    </div>
                  )}

                  <div>
                    <div style={{
                      fontSize: '13px',
                      color: theme.colors.textSecondary,
                      marginBottom: '4px',
                    }}>
                      Agent Response Time
                    </div>
                    <div style={{
                      fontSize: '15px',
                      color: theme.colors.textPrimary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      {query.agentResponseTime}
                    </div>
                  </div>
                </div>

                {/* Response Message */}
                {query.hasResponse && query.responseType && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: query.status === 'rejected' ? theme.colors.errorLight : theme.colors.successLight,
                    borderRadius: theme.borderRadius.small,
                    marginBottom: '16px',
                  }}>
                    <div style={{
                      fontSize: '14px',
                      color: query.status === 'rejected' ? theme.colors.error : theme.colors.success,
                      fontWeight: '500',
                    }}>
                      {query.responseType === 'partial' && '📧 Partial manuscript requested!'}
                      {query.responseType === 'full' && '📚 Full manuscript requested!'}
                      {query.responseType === 'pass' && 'Agent passed on this query'}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  paddingTop: '16px',
                  borderTop: `1px solid ${theme.colors.border}`,
                }}>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => window.location.href = `/writer/queries/${query.id}`}
                  >
                    View Details
                  </Button>
                  {query.hasResponse && query.responseType !== 'pass' && (
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => window.location.href = `/writer/queries/${query.id}#response`}
                    >
                      View Response
                    </Button>
                  )}
                  {query.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => handleWithdraw(query.id, query.agentName)}
                      style={{ color: theme.colors.error }}
                    >
                      Withdraw
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
